import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  FileText,
  Globe,
  Landmark,
  type LucideIcon,
  Mail,
  MapPin,
  Pause,
  Pencil,
  Play,
  Plug,
  ShieldCheck,
  Wallet,
} from 'lucide-react';

import { cn } from '@/shared/libs';
import { Badge, Button, CountryCodes, Dialog } from '@/shared/ui';
import {
  PROVIDER_STATUS_BADGE_VARIANT,
  PROVIDER_STATUS_I18N_KEY,
  ProviderName,
} from '@entities/Provider';
import type { Provider } from '@psp-aggregator/sdk';

export interface ProviderDetailsModalProps {
  onClose: () => void;
  onEdit?: () => void;
  onToggleStatus?: () => void;
  provider: Provider;
}

const Chip: FC<{ children: ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-md bg-foreground/5 px-2 py-0.5 text-[11px] font-medium text-foreground/80">
    {children}
  </span>
);

Chip.displayName = 'ProviderDetailChip';

const Chips: FC<{ icon: LucideIcon; label: string; values?: string[] }> = ({
  icon: Icon,
  label,
  values = [],
}) => (
  <div className="rounded-xl border border-border/40 bg-background/60 p-3">
    <p className="mb-1.5 flex items-center gap-1 text-[10px] tracking-wider text-muted-foreground uppercase">
      <Icon className="h-3 w-3" />
      {label}
    </p>
    <div className="flex flex-wrap gap-1">
      {values.length > 0 ? (
        values.map((value) => <Chip key={value}>{value}</Chip>)
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )}
    </div>
  </div>
);

Chips.displayName = 'ProviderDetailChips';

export const ProviderDetailsModal: FC<ProviderDetailsModalProps> = ({
  onClose,
  onEdit,
  onToggleStatus,
  provider,
}) => {
  const { t } = useTranslation();
  const status = provider.status ?? 'active';
  const isActive = status === 'active';
  const about = provider.full_description || provider.description;

  return (
    <Dialog title={provider.real_name} width={720} onClose={onClose}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {!!provider.logo_url && (
            <img
              alt=""
              className="h-12 w-12 shrink-0 rounded-xl border border-border object-cover"
              src={provider.logo_url}
            />
          )}
          <div className="min-w-0">
            <ProviderName alias={provider.alias} name={provider.real_name} />
            {!!provider.short_description && (
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                {provider.short_description}
              </p>
            )}
          </div>
        </div>
        <Badge variant={PROVIDER_STATUS_BADGE_VARIANT[status]}>
          {t(PROVIDER_STATUS_I18N_KEY[status])}
        </Badge>
      </div>
      <div className="mb-5 flex items-center gap-2">
        {!!onEdit && (
          <button
            aria-label={t('buttons.edit')}
            className="cursor-pointer rounded-lg p-1.5 text-muted-foreground hover:bg-indigo-500/10 hover:text-indigo-500"
            type="button"
            onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {!!onToggleStatus && (
          <button
            aria-label={isActive ? t('buttons.pause') : t('buttons.resume')}
            className={cn(
              'cursor-pointer rounded-lg p-1.5',
              isActive
                ? 'text-amber-600 hover:bg-amber-500/10 dark:text-amber-400'
                : 'text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400',
            )}
            type="button"
            onClick={onToggleStatus}>
            {isActive ? (
              <Pause className="h-3.5 w-3.5" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>
      <div className="max-h-[min(60dvh,calc(90dvh-10rem))] space-y-5 overflow-y-auto pr-1">
        <section>
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            <MapPin className="h-3.5 w-3.5" />
            {t('pages.admin.providers.sections.coverage')}
          </p>
          <div className="space-y-3">
            <div className="rounded-xl border border-border/40 bg-background/60 p-3">
              <CountryCodes codes={provider.countries ?? []} />
            </div>
            <Chips
              icon={Wallet}
              label={t('pages.admin.providers.fields.currencies')}
              values={provider.currencies}
            />
          </div>
        </section>
        <section>
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            <Plug className="h-3.5 w-3.5" />
            {t('pages.admin.providers.sections.capabilities')}
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Chips
              icon={Plug}
              label={t('pages.admin.providers.fields.integrationMethods')}
              values={provider.integration_methods}
            />
            <Chips
              icon={Wallet}
              label={t('pages.admin.providers.fields.paymentMethods')}
              values={provider.payment_methods}
            />
            <Chips
              icon={Briefcase}
              label={t('pages.admin.providers.fields.industries')}
              values={provider.industries}
            />
          </div>
        </section>
        {!!about && (
          <section>
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              <FileText className="h-3.5 w-3.5" />
              {t('pages.admin.providers.sections.about')}
            </p>
            <p className="rounded-xl border border-border/40 bg-background/60 p-4 text-sm whitespace-pre-wrap">
              {about}
            </p>
          </section>
        )}
        <section>
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            <ShieldCheck className="h-3.5 w-3.5" />
            {t('pages.admin.providers.sections.compliance')}
          </p>
          <div className="space-y-2">
            <Chips
              icon={Landmark}
              label={t('pages.admin.providers.fields.jurisdictions')}
              values={provider.jurisdictions}
            />
            <Chips
              icon={ShieldCheck}
              label={t('pages.admin.providers.fields.regulators')}
              values={provider.regulators}
            />
            {(provider.licenses ?? []).length > 0 && (
              <div className="rounded-xl border border-border/40 bg-background/60 p-3">
                <p className="mb-2 text-[10px] tracking-wider text-muted-foreground uppercase">
                  {t('pages.admin.providers.fields.licenses')}
                </p>
                <div className="space-y-1.5">
                  {provider.licenses?.map((license) => (
                    <div
                      key={`${license.name}-${license.document_url ?? ''}`}
                      className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate">{license.name || '—'}</span>
                      {!!license.document_url && (
                        <a
                          className="shrink-0 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
                          href={license.document_url}
                          rel="noreferrer"
                          target="_blank">
                          {t('buttons.view')}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
        <section>
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
            <Mail className="h-3.5 w-3.5" />
            {t('pages.admin.providers.sections.contacts')}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Chips
              icon={Mail}
              label={t('pages.admin.providers.fields.contactEmail')}
              values={provider.contacts?.email ? [provider.contacts.email] : []}
            />
            <Chips
              icon={Globe}
              label={t('pages.admin.providers.fields.website')}
              values={
                provider.contacts?.website ? [provider.contacts.website] : []
              }
            />
            <Chips
              icon={Mail}
              label={t('pages.admin.providers.fields.contactName')}
              values={
                provider.contacts?.contact_name
                  ? [provider.contacts.contact_name]
                  : []
              }
            />
            <Chips
              icon={Mail}
              label={t('pages.admin.providers.fields.contactPhone')}
              values={provider.contacts?.phone ? [provider.contacts.phone] : []}
            />
          </div>
        </section>
        {(provider.gallery_urls ?? []).length > 0 && (
          <section>
            <p className="mb-2 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('pages.admin.providers.sections.gallery')}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {provider.gallery_urls?.map((url) => (
                <img
                  key={url}
                  alt=""
                  className="aspect-square rounded-lg border border-border object-cover"
                  src={url}
                />
              ))}
            </div>
          </section>
        )}
      </div>
      <div className="mt-5 flex justify-end">
        <Button type="button" variant="secondary" onClick={onClose}>
          {t('buttons.close')}
        </Button>
      </div>
    </Dialog>
  );
};

ProviderDetailsModal.displayName = 'ProviderDetailsModal';
